import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";

import { SearchOutlined } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import { ModelStatusTypes } from "@prisma/client";
import { useRouter } from "next/router";

import { type AudioPlayerRef } from "@musicfy/components//AudioPlayer";
import Button from "@musicfy/components/Button";
import FullScreenTakeover from "@musicfy/components/FullScreenTakeover";
import Icon from "@musicfy/components/Icon";
import Input from "@musicfy/components/Input";
import { FEATURE_ROUTES } from "@musicfy/components/Navigation/constants";
import { Features } from "@musicfy/contants/Features";
import { useModels } from "@musicfy/libs/ModelsProvider/hooks";
import { type IModel } from "@musicfy/libs/ModelsProvider/IModels";
import { isModelTrending } from "@musicfy/libs/ModelsProvider/utils";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { canUseFeature } from "@musicfy/utils";

import EmptyState from "./EmptyState";
import GenderFilter, { type Gender } from "./GenderFilter";
import GenreFilter, { ALL_GENRES } from "./GenreFilter";
import LicenseFilter, { License } from "./LicenseFilter";
import ModelCard from "./ModelCard";
import ModelsCategories from "./ModelsCategories";
import SortFilter, { SortTypes } from "./SortFilter";
import { ModelCategory, type TModelCategory } from "../types";

interface ISelectArtistsTakeoverProps {
  isOpen: boolean;
  onClose: () => void;
  selectedModels: IModel[];
  setSelectedModels: React.Dispatch<React.SetStateAction<IModel[]>>;
  selectedLimit: number;
  modelsCategoryFromUrl?: TModelCategory;
}

const MODELS_CATEGORY_TO_TITLE_SUFFIX = {
  [ModelCategory.COPYRIGHT_FREE]: "a copyright-free voice",
  [ModelCategory.VOCALS]: "an artist",
  [ModelCategory.INSTRUMENTALS]: "an instrumental",
  [ModelCategory.TRENDING]: "a trending voice",
  [ModelCategory.FAVORITES]: "a favorited voice",
  [ModelCategory.MY_VOICES]: "your voices",
};

const SelectedModelsPopup: React.FC<{
  selected: number;
  selectedLimit: number;
  closeSelectedArtistTakeover?: () => void;
  clearSelectedArtistTakeover?: () => void;
  modelsCategory?: TModelCategory;
}> = ({
  selected,
  selectedLimit,
  closeSelectedArtistTakeover,
  clearSelectedArtistTakeover,
  modelsCategory,
}) => {
  return (
    <div className="sticky mt-4 z-40 w-full rounded-8 border border-solid border-white/20 bg-gray-1000/50 p-6 text-white backdrop-blur-2xl bottom-2">
      <div className="flex lg:flex-row flex-col gap-4 items-center justify-between">
        <div>
          <Tooltip
            arrow
            title={`You can convert an audio clip to multiple ${
              modelsCategory || "voices"
            } at once.`}
          >
            <div className="flex items-center gap-2 cursor-pointer text-20">
              <Icon name="information" />
              <div
                className="capitalize"
                aria-label={`${selected}/${selectedLimit} Voices Selected`}
              >
                {selected}/{selectedLimit} Voices Selected
              </div>
            </div>
          </Tooltip>
        </div>
        <div className="flex w-full lg:w-fit items-center gap-4">
          <Button
            onClick={clearSelectedArtistTakeover}
            className="w-full xl:w-48"
            variant="outlined"
            style="secondary"
            aria-label="Clear Selected Artists"
          >
            Clear
          </Button>
          <Button
            onClick={closeSelectedArtistTakeover}
            className="w-full xl:w-48"
            aria-label="Done Selecting Artists"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

const SelectArtistsTakeover: React.FC<ISelectArtistsTakeoverProps> = ({
  isOpen,
  onClose,
  selectedModels,
  setSelectedModels,
  selectedLimit,
  modelsCategoryFromUrl,
}) => {
  const { subscription } = useSubscriptionContext();

  const router = useRouter();

  const { models } = useModels();

  const audioPlayersRef = useRef<AudioPlayerRef[]>([]);

  const [genreFilters, setGenreFilters] = useState<string[]>([ALL_GENRES]);
  const [genderFilter, setGenderFilter] = useState<Gender | null>(null);
  const [licenseFilter, setLicenseFilter] = useState<License | null>(null);
  const [modelSearchTerm, setModelSearchTerm] = useState<string>("");
  const [sortType, setSelectedSortType] = useState<SortTypes>(
    SortTypes.trending
  );
  const [selectedModelsCategory, setSelectedModelsCategory] =
    useState<TModelCategory>(ModelCategory.VOCALS);

  useEffect(() => {
    if (!modelsCategoryFromUrl) {
      return;
    }
    setSelectedModelsCategory(modelsCategoryFromUrl);
  }, [modelsCategoryFromUrl]);

  const filteredCategoryModels = useMemo(() => {
    if (selectedModelsCategory === ModelCategory.COPYRIGHT_FREE) {
      return models.filter(
        (model) =>
          model.status === ModelStatusTypes.completed &&
          !model.isInstrumental &&
          model.isCopyrightFree &&
          !model.isCommunityModel
      );
    }

    if (selectedModelsCategory === ModelCategory.VOCALS) {
      return models.filter(
        (model) =>
          model.status === ModelStatusTypes.completed && !model.isInstrumental
      );
    }

    if (selectedModelsCategory === ModelCategory.INSTRUMENTALS) {
      return models.filter(
        (model) =>
          model.status === ModelStatusTypes.completed && model.isInstrumental
      );
    }

    // Same selection as the home "Trending Voices" carousel (minus the slice limit).
    if (selectedModelsCategory === ModelCategory.TRENDING) {
      return models.filter(
        (model) =>
          model.status === ModelStatusTypes.completed &&
          !model.isInstrumental &&
          !!model.usageCount &&
          model.usageCount > 0 &&
          !model.isPrivate &&
          isModelTrending(model)
      );
    }

    if (selectedModelsCategory === ModelCategory.FAVORITES) {
      return models.filter(
        (model) =>
          model.status === ModelStatusTypes.completed && model.isFavorited
      );
    }

    if (selectedModelsCategory === ModelCategory.MY_VOICES) {
      return models.filter(
        (model) => model.status === ModelStatusTypes.completed && model.isOwner
      );
    }

    return [];
  }, [models, selectedModelsCategory]);

  const canUseLockedModels = canUseFeature(
    Features.LOCKED_MODELS,
    subscription
  );

  const filteredModels = useMemo(() => {
    return filteredCategoryModels
      .filter((model) => {
        if (
          model.artist.toLowerCase().includes(modelSearchTerm.toLowerCase())
        ) {
          return true;
        }
      })
      .filter((model) => {
        if (model.isInstrumental && licenseFilter === License.ROYALTY_FREE) {
          return true;
        }

        if (licenseFilter === License.ROYALTY_FREE) {
          return model.isCopyrightFree && !model.isCommunityModel;
        }

        if (licenseFilter === License.PARODY) {
          return !model.isCopyrightFree;
        }

        if (licenseFilter === License.COPYRIGHTED) {
          return model.isCopyrightFree && model.isCommunityModel;
        }

        return true;
      })
      .filter((model) => {
        if (model.isInstrumental) {
          return true;
        }
        return !!genderFilter ? model.gender === genderFilter : true;
      })
      .filter((model) => {
        if (model.isInstrumental) {
          return true;
        }

        return (
          !!genreFilters.find(
            (genre) => genre.toLocaleLowerCase() === model.genre?.toLowerCase()
          ) || genreFilters.includes(ALL_GENRES)
        );
      })
      .map((model) => ({
        ...model,
        isTrending: isModelTrending(model),
      }))
      .sort((a, b) => {
        if (a.isLocked && !b.isLocked && !canUseLockedModels) {
          return 1;
        } else if (!a.isLocked && b.isLocked && !canUseLockedModels) {
          return -1;
        }

        switch (sortType) {
          case SortTypes.trending:
            return (b?.trendingScore || 0) - (a?.trendingScore || 0);
          case SortTypes.newest:
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          case SortTypes.mostPopular:
          default:
            return (b?.usageCount || 0) - (a?.usageCount || 0);
        }
      });
  }, [
    filteredCategoryModels,
    modelSearchTerm,
    licenseFilter,
    genderFilter,
    genreFilters,
    canUseLockedModels,
    sortType,
  ]);
  const canSelectMultipleModels = canUseFeature(
    Features.SELECT_MULTIPLE_MODELS,
    subscription
  );

  const onModelClick = useCallback(
    (model: IModel) => {
      const identifier =
        !model.isPrivate && !model.isCommunityModel ? model.artist : model.id;
      const secondaryIdentifier =
        !model.isPrivate && !model.isCommunityModel ? null : model.artist;

      if (!canSelectMultipleModels || selectedModels.length === 0) {
        setSelectedModels([model]);
        onClose();

        // Keep models_category so Create nav (Copyright-Free / Instrumentals)
        // stays highlighted after the picker closes.
        const categoryFromUrl = Array.isArray(router.query.models_category)
          ? router.query.models_category[0]
          : router.query.models_category;
        const navCategory =
          categoryFromUrl ||
          (modelsCategoryFromUrl &&
          (modelsCategoryFromUrl === ModelCategory.COPYRIGHT_FREE ||
            modelsCategoryFromUrl === ModelCategory.INSTRUMENTALS)
            ? modelsCategoryFromUrl
            : undefined);

        const voicePath = `${FEATURE_ROUTES.voice}/${identifier}${
          secondaryIdentifier ? `/${secondaryIdentifier}` : ""
        }`;
        const href = navCategory
          ? `${voicePath}?models_category=${encodeURIComponent(navCategory)}`
          : voicePath;
        const currentPath = router.asPath.split("?")[0] ?? router.asPath;

        router.replace(href, undefined, {
          scroll: false,
          shallow: currentPath === FEATURE_ROUTES.voice ? false : true,
        });
        return;
      }

      const isModelSelected = selectedModels.some(
        (selectedModel) => selectedModel.id === model.id
      );

      if (selectedModels.length >= selectedLimit && !isModelSelected) {
        return;
      }

      setSelectedModels((prev) => {
        if (isModelSelected) {
          return prev.filter((selectedModel) => selectedModel.id !== model.id);
        }

        return [...prev, model];
      });
    },
    [
      canSelectMultipleModels,
      modelsCategoryFromUrl,
      onClose,
      router,
      selectedLimit,
      selectedModels,
      setSelectedModels,
    ]
  );

  const selectionSearchPlaceholder = `Search for ${MODELS_CATEGORY_TO_TITLE_SUFFIX[selectedModelsCategory]}`;

  return (
    <FullScreenTakeover open={isOpen} onClose={onClose} keepSidebarVisible>
      <ModelsCategories
        selectedModelsCategory={selectedModelsCategory}
        setSelectedModelsCategory={setSelectedModelsCategory}
      />
      {selectedModelsCategory === ModelCategory.COPYRIGHT_FREE && (
        <div className="mt-6 rounded-8 border border-green/30 bg-green/10 px-4 py-3 text-14 text-white/90">
          Start here — these voices are copyright-free and safe for commercial
          use. Save any voice with the heart icon to build your own favorites.
        </div>
      )}
      {selectedModelsCategory === ModelCategory.INSTRUMENTALS && (
        <div className="mt-6 rounded-8 border border-violet/30 bg-violet/10 px-4 py-3 text-14 text-white/90">
          Browse royalty-free instrumentals you can use in your projects without
          copyright concerns.
        </div>
      )}
      {selectedModelsCategory === ModelCategory.TRENDING && (
        <div className="mt-6 rounded-8 border border-gold/30 bg-gold/10 px-4 py-3 text-14 text-white/90">
          Recently popular and newly shipped voices — a shorter list so weekly
          releases are easier to find.
        </div>
      )}
      {selectedModelsCategory === ModelCategory.FAVORITES && (
        <div className="mt-6 rounded-8 border border-blue/30 bg-blue/10 px-4 py-3 text-14 text-white/90">
          Your saved voices. Tap the heart on any voice to add or remove it from
          Favorites.
        </div>
      )}
      <div className="mt-6" />
      <Input
        className="!text-16"
        value={modelSearchTerm}
        onChange={(e) => {
          e.preventDefault();
          setModelSearchTerm(e.target.value);
        }}
        placeholder={selectionSearchPlaceholder}
        preIcon={<SearchOutlined />}
      />

      {selectedModelsCategory !== ModelCategory.INSTRUMENTALS && (
        <div className="flex flex-col gap-4">
          <div className="mt-6 flex gap-4 items-center lg:flex-row flex-col">
            <div className="flex items-center gap-4">
              {selectedModelsCategory !== ModelCategory.COPYRIGHT_FREE && (
                <div>
                  <LicenseFilter
                    licenseFilter={licenseFilter}
                    setLicenseFilter={setLicenseFilter}
                  />
                </div>
              )}
              <div>
                <GenderFilter
                  genderFilter={genderFilter}
                  setGenderFilter={setGenderFilter}
                />
              </div>
            </div>
            <div className="mr-auto lg:ml-auto lg:mx-0">
              <SortFilter
                sortType={sortType}
                setSelectedSortType={setSelectedSortType}
              />
            </div>
          </div>
          <GenreFilter
            genreFilters={genreFilters}
            setGenreFilters={setGenreFilters}
          />
        </div>
      )}

      {filteredModels.length !== 0 ? (
        <div className="mt-9 grid grid-cols-2 gap-4 lg:grid-cols-3 lg:gap-6 xl:grid-cols-5 xl:gap-9">
          {filteredModels.map((model, index) => {
            const isModelLocked = model.isLocked && !canUseLockedModels;
            return (
              <ModelCard
                key={model.id}
                model={model}
                onSelectModel={onModelClick}
                isSelected={
                  !!selectedModels.find((selected) => selected?.id === model.id)
                }
                modelIndex={index}
                audioPlayersRef={audioPlayersRef}
                isLocked={isModelLocked}
                isTrending={model.isTrending}
              />
            );
          })}
        </div>
      ) : (
        <div className="mt-6">
          <EmptyState
            title={
              selectedModelsCategory === ModelCategory.FAVORITES
                ? "No favorite voices yet"
                : undefined
            }
            subtitle={
              selectedModelsCategory === ModelCategory.FAVORITES
                ? "Tap the heart icon on any voice to save it here"
                : undefined
            }
          />
        </div>
      )}
      {!!selectedModels.length && canSelectMultipleModels && (
        <SelectedModelsPopup
          selected={selectedModels.length}
          selectedLimit={selectedLimit}
          closeSelectedArtistTakeover={onClose}
          clearSelectedArtistTakeover={() => setSelectedModels([])}
          modelsCategory={selectedModelsCategory}
        />
      )}
    </FullScreenTakeover>
  );
};

export default SelectArtistsTakeover;
